import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@wmgtech/legato'
import { useQuery, useReactiveVar } from '@apollo/client'

import { FormAdminContainer } from 'components'
import { LabelForm } from 'modules/Labels/components'
import { useLoading } from 'shared/hooks'
import { useServerError } from 'shared/hooks'
import { countryCodeListVar } from 'modules/Labels/store'
import { GET_COUNTRY_LIST } from 'modules/Labels/graphql/queries'
import { useCreateLabel } from 'modules/Labels/graphql/mutations'
import { defaultValues } from 'modules/Labels/constants'

import { ICreateLabel } from 'modules/Labels/components/LabelForm/LabelForm.props'
import { TOption } from 'shared/types/SelectOptions'

const CreateLabelContainer = () => {
  const navigate = useNavigate()
  const countryCodeList = useReactiveVar(countryCodeListVar)

  const { mutate: createLabel, loading: isLoading, error: creationError } = useCreateLabel()

  const { loading: isCountryCodesListLoading, error: countryCodesError } = useQuery(GET_COUNTRY_LIST, {
    onCompleted: (data) => countryCodeListVar(data.getCountryList || [])
  })

  const countryOptions = useMemo(
    (): TOption[] =>
      countryCodeList.map((item) => ({
        key: item.code,
        title: `${item.code} - ${item.name}`
      })),
    [countryCodeList]
  )

  const onSubmit = useCallback(
    (values: ICreateLabel) => {
      createLabel({
        variables: {
          input: {
            alias: values.alias,
            name: values.name,
            country_codes: values.country_codes,
            ...(values.max_radio_stations && { max_radio_stations: values.max_radio_stations })
          }
        }
      }).then(
        ({
          data: {
            createLabel: { id }
          }
        }) => {
          if (id) {
            navigate('/admin/labels')
          }
        }
      )
    },
    [createLabel, navigate]
  )

  useServerError([countryCodesError, creationError])
  useLoading({ isLoading: isCountryCodesListLoading || isLoading })

  return (
    <FormAdminContainer
      backLink="/admin/labels"
      actions={
        <Button containerStyle="solid" colorType="secondary-black" label={'Save'} form="label-form" htmlType="submit" />
      }
    >
      <LabelForm countryOptions={countryOptions} onSubmit={onSubmit} defaultValues={defaultValues} />
    </FormAdminContainer>
  )
}

export default CreateLabelContainer
